import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Hash, Loader2, Lock, Mail, PhoneCall } from "lucide-react";
import { useLocation } from "react-router-dom";
import oneSipLogo from "@/assets/onesip-logo-primary.png";

const BG = "/login-wallpaper.jpg";

export default function Login() {
  const { login, loginAgent, loginExtension, error } = useAuth();
  const location = useLocation();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const supervisorAccess = pathParts.length >= 2 && pathParts[1] === "supervisores";
  const extensionAccess = pathParts.length >= 2 && pathParts[1] === "ramal-web";
  const tenantLabel = pathParts.length >= 2 && ["agentes", "supervisores", "ramal-web"].includes(pathParts[1]) ? pathParts[0] : "";
  const agentAccess = !supervisorAccess && !extensionAccess && (location.pathname.startsWith("/agente") || (pathParts.length >= 2 && pathParts[1] === "agentes"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [extension, setExtension] = useState("");
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return document.documentElement.dataset.theme || localStorage.getItem("voxyra-theme") || "light";
  });

  useEffect(() => {
    const refreshTheme = () => {
      setTheme(document.documentElement.dataset.theme || localStorage.getItem("voxyra-theme") || "light");
    };
    refreshTheme();
    window.addEventListener("storage", refreshTheme);
    const timer = window.setInterval(refreshTheme, 500);
    return () => {
      window.removeEventListener("storage", refreshTheme);
      window.clearInterval(timer);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (agentAccess) {
      await loginAgent(email, password, extension);
    } else if (extensionAccess) {
      await loginExtension(email, password);
    } else {
      await login(email, password);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-start" data-testid="login-page">
      <div className="absolute inset-0" style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div
        className="absolute inset-0"
        style={{
          background:
            theme === "black"
              ? "linear-gradient(90deg, rgba(8,11,18,0.96) 0%, rgba(8,11,18,0.88) 40%, rgba(8,11,18,0.58) 100%)"
              : theme === "futuristic"
                ? "linear-gradient(90deg, rgba(5,10,24,0.95) 0%, rgba(7,16,38,0.88) 42%, rgba(8,20,51,0.56) 100%)"
                : "linear-gradient(90deg, rgba(246,247,249,0.98) 0%, rgba(246,247,249,0.92) 40%, rgba(246,247,249,0.55) 100%)",
        }}
      />

      <div className="relative z-10 w-full max-w-md ml-4 sm:ml-10 lg:ml-16 p-6 sm:p-10 fade-in">
        <div className="mb-10 flex flex-col items-center text-center">
          <div className="w-full max-w-[19rem] h-24 flex items-center justify-center shrink-0">
              <img src={oneSipLogo} alt="OneSIP by Voxyra One" className="w-full h-full object-contain" />
          </div>
        </div>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Acesso restrito</div>
          <div className="text-lg text-[color:var(--text-secondary)]">
            {extensionAccess
              ? "Acesso do webphone do ramal com audio e video"
              : agentAccess
                ? "Acesso exclusivo para agentes"
                : supervisorAccess
                  ? "Acesso exclusivo para supervisao"
                  : "Entre com suas credenciais de administracao"}
          </div>
          {tenantLabel ? (
            <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[color:var(--accent-blue)] font-semibold">
              Empresa: {tenantLabel}
            </div>
          ) : null}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-2">
              {extensionAccess ? "Usuario ou email" : agentAccess ? "Usuario ou email" : "Email"}
            </label>
            <div className="relative">
              {extensionAccess ? (
                <PhoneCall size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              ) : (
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              )}
              <input
                data-testid="login-email"
                type={agentAccess || extensionAccess ? "text" : "email"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="sbc-input pl-9 font-mono"
                placeholder={extensionAccess ? "usuario@empresa.com ou usuario.login" : agentAccess ? "agente@empresa.com ou agente.login" : "operador@empresa.com"}
                required
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-2">
              Senha
            </label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="sbc-input pl-9 font-mono"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {agentAccess ? (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-2">
                Ramal de operacao
              </label>
              <div className="relative">
                <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
                <input
                  data-testid="login-extension"
                  type="text"
                  inputMode="numeric"
                  value={extension}
                  onChange={(e) => setExtension(e.target.value.replace(/\D/g, ""))}
                  className="sbc-input pl-9 font-mono"
                  placeholder="9160"
                  required
                />
              </div>
            </div>
          ) : null}

          {error ? (
            <div data-testid="login-error" className="text-xs text-[color:var(--accent-red)] font-mono border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">
              ! {error}
            </div>
          ) : null}

          <button data-testid="login-submit" type="submit" disabled={loading} className="sbc-btn sbc-btn-primary w-full justify-center h-10 text-sm mt-2">
            {loading ? <Loader2 size={14} className="animate-spin" /> : null}
            {loading ? "Autenticando..." : extensionAccess ? "Entrar no webphone" : "Autenticar sessao"}
          </button>
        </form>
      </div>
    </div>
  );
}
